import { api } from '../api.js';
import { getTenantId as getAuthTenantId } from '../utils/token.js';
import { resolveDataTenantId } from '../utils/platformViewTenant.js';
import { getFarms } from './farms.js';
import { getLandParcels } from './landParcels.js';
import {
  axiosToMiniBody,
  buildLoggedOutHomeFarmPreview,
  buildEmptyHomeFarmPreview,
  computeHomeFarmPreviewFromBodies
} from '../utils/homeFarmPreview.js';
import { loadHomePrimaryFarmId } from '../utils/homePrimaryFarmPrefs.js';

/**
 * 首页 KPI 用预警数：只拉未处理预警
 */
export async function getHomeFarmWarnings(tenantId) {
  const tid = tenantId || getAuthTenantId();
  try {
    const res = await api.get('/warnings/records/unresolved', { params: { tenantId: tid } });
    const body = axiosToMiniBody(res) || {};
    const list = Array.isArray(body.records)
      ? body.records
      : Array.isArray(body.data)
        ? body.data
        : [];
    return { success: true, data: list };
  } catch {
    return { success: true, data: [] };
  }
}

function isAborted(signal) {
  return Boolean(signal && signal.aborted);
}

function resolveTid(options = {}) {
  const tenantId = options.useJwtTenant ? getAuthTenantId() : resolveDataTenantId();
  return tenantId || 'default';
}

/**
 * 首屏关键：仅农场列表 + 主农场（用于英雄/封面/选项），一次并行完成。
 * @returns {Promise<{ preview: object, farmsBody: any, preferredFarmId: string, tid: string, hasFarms: boolean }|null>}
 */
export async function loadHomeFarmPreviewShell(loggedIn, options = {}) {
  if (!loggedIn) {
    const preview = buildLoggedOutHomeFarmPreview();
    return {
      preview,
      farmsBody: null,
      preferredFarmId: '',
      tid: 'default',
      hasFarms: false,
    };
  }

  const tid = resolveTid(options);

  try {
    const [farmsAxios, preferredFarmId] = await Promise.all([
      getFarms({ tenantId: tid }),
      loadHomePrimaryFarmId().catch(() => ''),
    ]);
    if (isAborted(options.signal)) return null;

    const farmsBody = axiosToMiniBody(farmsAxios);
    const hasFarms =
      farmsBody?.success &&
      Array.isArray(farmsBody.data) &&
      farmsBody.data.some((f) => f && f.id != null && String(f.id).trim() !== '');

    const preview = computeHomeFarmPreviewFromBodies({
      farmsBody,
      statsBody: null,
      warnBody: { success: true, data: [] },
      landBody: null,
      deviceBody: null,
      tasksBody: null,
      preferredFarmId,
    });

    return { preview, farmsBody, preferredFarmId, tid, hasFarms };
  } catch (e) {
    console.warn('首页农场壳层加载失败:', e);
    return {
      preview: buildEmptyHomeFarmPreview(),
      farmsBody: null,
      preferredFarmId: '',
      tid,
      hasFarms: false,
    };
  }
}

/**
 * 静默补齐：种植统计 / 预警 / 地块 / 设备汇总 / 农事任务。
 */
export async function enrichHomeFarmPreview(shellCtx, options = {}) {
  if (!shellCtx || !shellCtx.farmsBody) {
    return shellCtx?.preview || buildEmptyHomeFarmPreview();
  }
  if (isAborted(options.signal)) return shellCtx.preview;

  const tid = shellCtx.tid || resolveTid(options);

  try {
    const [statsAxios, warnBody, landAxios, deviceAxios, tasksAxios, planAxios] = await Promise.all([
      api.get(`/planted-crops/statistics`, { params: { tenantId: tid } }).catch(() => null),
      getHomeFarmWarnings(tid),
      getLandParcels({ tenantId: tid }).catch(() => null),
      api.get('/farm-smart-device/summary', { params: { tenantId: tid } }).catch(() => null),
      api.get('/field-tasks', { params: { tenantId: tid } }).catch(() => null),
      api.get('/planting-plans', { params: { tenantId: tid, page: 0, size: 500 } }).catch(() => null),
    ]);
    if (isAborted(options.signal)) return shellCtx.preview;

    return computeHomeFarmPreviewFromBodies({
      farmsBody: shellCtx.farmsBody,
      statsBody: axiosToMiniBody(statsAxios),
      warnBody,
      landBody: axiosToMiniBody(landAxios),
      deviceBody: axiosToMiniBody(deviceAxios),
      tasksBody: axiosToMiniBody(tasksAxios),
      planBody: axiosToMiniBody(planAxios),
      preferredFarmId: shellCtx.preferredFarmId,
    });
  } catch (e) {
    console.warn('首页农场 KPI 静默补齐失败:', e);
    return shellCtx.preview;
  }
}

/**
 * 一次性完整加载（兼容旧调用：管理端切主农场等）。
 */
export async function loadHomeFarmPreview(loggedIn, options = {}) {
  if (!loggedIn) return buildLoggedOutHomeFarmPreview();
  const shell = await loadHomeFarmPreviewShell(loggedIn, options);
  if (!shell) return buildEmptyHomeFarmPreview();
  return enrichHomeFarmPreview(shell, options);
}

/**
 * 渐进：shell 回调后返回壳层结果（KPI 需另调 enrich，或本函数继续 await 全量）。
 * @deprecated 优先用 loadHomeFarmPreviewShell + enrichHomeFarmPreview
 */
export async function loadHomeFarmPreviewProgressive(loggedIn, options = {}) {
  const shell = await loadHomeFarmPreviewShell(loggedIn, options);
  if (!shell) return buildEmptyHomeFarmPreview();
  options.onShell?.(shell.preview);
  return enrichHomeFarmPreview(shell, options);
}
